import type { CoreError } from '@ingenico/api-sdk-core/common';
import type {
  ConfigurationResourceApplicationItemBffV2Api,
  ConfigurationResourceDefinitionBffV2Api,
  ConfigurationResourceDefinitionMinimalV2Api,
  ConfigurationResourceSoftwareItemMinimalV2Api,
  ConfigurationResourceSystemPackageItemBffV2Api,
  CreateConfigurationV2MutationRequestApi,
  GenericIdApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import type {
  ConfigurationResourceApplicationVersionApi,
  ConfigurationResourceCreateApi,
  ConfigurationResourceOsVersionApi,
  ConfigurationResourceReadApi,
  ConfigurationResourceSystemPackageVersionApi,
} from '@ingenico/api-sdk-device-management-public';
// This screen may be broken because there's disruptive changes on the public API for this types. Upgrading API-ADK of device management public will be tricky
import { enumConfigurationType } from '@ingenico/api-sdk-device-management-public';
import type { ConfigurationResourceRemoteKeyInjectionFront } from '@ingenico/react-configuration-generator';
import { Level, useShowNotification } from '@ingenico/react-notifications';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { BaseConfiguration } from '../domain/models/baseConfiguration.model';
import type { ICustomConfigurationProfileRequest } from '../pages/create-or-edit-configuration-profile-page/CreateOrEditConfigurationProfilePage.model';
import { useDeviceManagementError } from './useDeviceManagementError.hook';

export function useCreateOrDuplicateConfigurationProfile({
  isDuplicateAction,
  configuration,
  toggleModalClose,
}: {
  readonly isDuplicateAction: boolean;
  readonly configuration: ConfigurationResourceReadApi | null | undefined;
  readonly toggleModalClose: () => void;
}): {
  readonly handleSuccess: (response: ConfigurationResourceReadApi) => void;
  readonly handleError: (error?: CoreError) => void;
  readonly buildConfigurationRequestData: (formValues: {
    readonly name: string;
    readonly description: string;
  }) => ConfigurationResourceCreateApi;
  readonly buildConfigurationRequestDataForV2: (
    formValues: ICustomConfigurationProfileRequest,
  ) => CreateConfigurationV2MutationRequestApi;
  readonly duplicateConfigurationRequestDataForV2: (
    formValues: ICustomConfigurationProfileRequest,
    definition?: ConfigurationResourceDefinitionBffV2Api | ConfigurationResourceDefinitionMinimalV2Api | null,
  ) => CreateConfigurationV2MutationRequestApi;
} {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE, ALL_LNGS_NAMESPACES.COMMON]);
  const showNotification = useShowNotification();
  const navigate = useNavigate();
  const { getErrorMessage } = useDeviceManagementError();

  const handleSuccess = useCallback(
    (response: ConfigurationResourceReadApi) => {
      const successMessageKey = isDuplicateAction ? 'success.default.duplicate' : 'success.default.create';
      showNotification({
        level: Level.Success,
        content: t(successMessageKey, { ns: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE }),
      });
      if (response?.id && !isDuplicateAction) {
        void navigate(response.id);
      } else {
        void navigate(`../${response.id}`);
      }
      toggleModalClose();
    },
    [isDuplicateAction, showNotification, t, navigate, toggleModalClose],
  );

  const handleError = useCallback(
    (error?: CoreError) => {
      const errorMessageKey = isDuplicateAction ? 'error.default.duplicate' : 'error.default.create';
      if (isDuplicateAction) {
        showNotification({
          level: Level.Error,
          content: error?.data?.title ?? t(errorMessageKey, { ns: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE }),
        });
      } else {
        showNotification({
          content: error
            ? getErrorMessage(error)
            : t(errorMessageKey, { ns: ALL_LNGS_NAMESPACES.CONFIGURATION_PROFILE }),
          level: Level.Error,
        });
      }
    },
    [isDuplicateAction, showNotification, t, getErrorMessage],
  );

  const formatSelectedApplicationVersions = useCallback(
    (remoteKeyInjectionValues: ConfigurationResourceApplicationVersionApi[] | undefined) => {
      return remoteKeyInjectionValues?.map((value) => {
        const typedValue = value as ConfigurationResourceApplicationVersionApi & { isApplicationActive?: boolean };
        const { isApplicationActive, ...rest } = typedValue;
        return isApplicationActive ? typedValue : { ...rest, isApplicationActive };
      });
    },
    [],
  );

  const formatSelectedOsVersions = useCallback((remoteKeyInjectionValues: ConfigurationResourceOsVersionApi) => {
    const typedValue = remoteKeyInjectionValues as ConfigurationResourceOsVersionApi & {
      isOsActive?: boolean;
      osInheritanceStatus?: string;
    };
    const { isOsActive, osInheritanceStatus } = typedValue;
    return isOsActive ? typedValue : { ...typedValue, isOsActive, osInheritanceStatus };
  }, []);

  const formatSelectedSystemPackageVersions = useCallback(
    (remoteKeyInjectionValues?: ConfigurationResourceSystemPackageVersionApi[]) => {
      return remoteKeyInjectionValues?.map((value) => {
        const typedValue = value as ConfigurationResourceSystemPackageVersionApi & { isSystemPackageActive?: boolean };
        const { isSystemPackageActive, ...rest } = typedValue;
        return isSystemPackageActive ? typedValue : { ...rest, isSystemPackageActive };
      });
    },
    [],
  );

  const buildConditions = useCallback(
    (baseConfiguration: BaseConfiguration) => {
      const hasRemoteKeyInjection = isDuplicateAction && baseConfiguration?.remoteKeyInjection;
      const hasSelectedAppVersions =
        isDuplicateAction &&
        baseConfiguration?.selectedApplicationVersions &&
        baseConfiguration?.selectedApplicationVersions?.length > 0;
      const hasSelectedOsVersion = isDuplicateAction && baseConfiguration?.selectedOsVersion;
      const hasSelectedSysPackages =
        isDuplicateAction &&
        baseConfiguration?.selectedSystemPackageVersions &&
        baseConfiguration?.selectedSystemPackageVersions?.length > 0;

      return {
        hasRemoteKeyInjection,
        hasSelectedAppVersions,
        hasSelectedOsVersion,
        hasSelectedSysPackages,
      };
    },
    [isDuplicateAction],
  );

  const buildConfigurationRequestData = useCallback(
    (formValues: { readonly name: string; readonly description: string }) => {
      const baseConfiguration = isDuplicateAction ? { ...configuration, ...formValues } : { ...formValues };
      const { hasRemoteKeyInjection, hasSelectedAppVersions, hasSelectedOsVersion, hasSelectedSysPackages } =
        buildConditions(baseConfiguration);

      return {
        configuration: {
          description: baseConfiguration.description,
          name: baseConfiguration.name,
          ...(hasRemoteKeyInjection && { remoteKeyInjection: baseConfiguration.remoteKeyInjection }),
          ...(hasSelectedAppVersions && {
            selectedApplicationVersions: formatSelectedApplicationVersions(
              baseConfiguration.selectedApplicationVersions,
            ),
          }),
          ...(hasSelectedOsVersion && {
            selectedOsVersion:
              baseConfiguration.selectedOsVersion && formatSelectedOsVersions(baseConfiguration.selectedOsVersion),
          }),
          ...(hasSelectedSysPackages && {
            selectedSystemPackageVersions: formatSelectedSystemPackageVersions(
              baseConfiguration.selectedSystemPackageVersions,
            ),
          }),
          type: enumConfigurationType.STANDARD,
        },
        targets: [],
      };
    },
    [
      isDuplicateAction,
      configuration,
      buildConditions,
      formatSelectedApplicationVersions,
      formatSelectedOsVersions,
      formatSelectedSystemPackageVersions,
    ],
  );

  const buildConfigurationRequestDataForV2 = useCallback((formValues: ICustomConfigurationProfileRequest) => {
    return {
      definition: {
        compositions: [],
      },
      name: formValues.name,
      ...(formValues.description?.trim() ? { description: formValues.description.trim() } : {}),
    };
  }, []);

  const getApplicationVersionsV2 = useCallback(
    (
      applicationVersionsValues:
        | ConfigurationResourceApplicationItemBffV2Api[]
        | ConfigurationResourceSoftwareItemMinimalV2Api[],
    ): ConfigurationResourceSoftwareItemMinimalV2Api[] => {
      return (applicationVersionsValues ?? [])?.map((value) => {
        return {
          type: value.type,
          id: value.id,
          order: value.order,
        } as ConfigurationResourceSoftwareItemMinimalV2Api;
      });
    },
    [],
  );
  const getSystemPackageVersionsV2 = useCallback(
    (
      systemPackageVersionsValues?:
        | ConfigurationResourceSoftwareItemMinimalV2Api[]
        | ConfigurationResourceSystemPackageItemBffV2Api[],
    ) => {
      return (systemPackageVersionsValues ?? [])?.map((value) => {
        return {
          type: value.type,
          id: value.id,
          order: value.order,
        } as ConfigurationResourceSoftwareItemMinimalV2Api;
      });
    },
    [],
  );

  const getRemoteKeyInjectionV2 = useCallback(
    (remoteKeyInjectionValues?: ConfigurationResourceRemoteKeyInjectionFront[] | GenericIdApi[]): GenericIdApi[] => {
      return (remoteKeyInjectionValues ?? [])?.map((value) => {
        if (typeof value === 'string') {
          return value;
        } else if ('id' in value) {
          return value.id;
        }
        return value;
      });
    },
    [],
  );
  const buildConditionsV2 = useCallback(
    (definition?: ConfigurationResourceDefinitionBffV2Api | ConfigurationResourceDefinitionMinimalV2Api | null) => {
      const hasRemoteKeyInjection = isDuplicateAction && definition?.configuration?.remoteKeyInjection;
      const hasSelectedAppVersions =
        isDuplicateAction &&
        definition?.configuration?.applicationVersions &&
        definition?.configuration?.applicationVersions?.length > 0;
      const hasSelectedOsVersion = isDuplicateAction && definition?.configuration?.osVersion;
      const hasSelectedSysPackages =
        isDuplicateAction &&
        definition?.configuration?.systemPackageVersions &&
        definition?.configuration?.systemPackageVersions?.length > 0;

      return {
        hasRemoteKeyInjection,
        hasSelectedAppVersions,
        hasSelectedOsVersion,
        hasSelectedSysPackages,
      };
    },
    [isDuplicateAction],
  );

  const duplicateConfigurationRequestDataForV2 = useCallback(
    (
      formValues: { readonly name: string; readonly description?: string },
      definition?: ConfigurationResourceDefinitionBffV2Api | ConfigurationResourceDefinitionMinimalV2Api | null,
    ) => {
      if (!definition) {
        return {
          name: formValues.name,
          definition: {
            compositions: [],
          },
          description: formValues.description,
        };
      }
      const { hasRemoteKeyInjection, hasSelectedAppVersions, hasSelectedOsVersion, hasSelectedSysPackages } =
        buildConditionsV2(definition);
      const data: CreateConfigurationV2MutationRequestApi = {
        ...(formValues.description && { description: formValues.description }),
        name: formValues.name,
        definition: {
          compositions: [],
          configuration: {
            ...(hasSelectedAppVersions
              ? {
                  applicationVersions: getApplicationVersionsV2(definition?.configuration?.applicationVersions ?? []),
                }
              : { applicationVersions: [] }),
            ...(hasSelectedOsVersion && {
              osVersion: definition?.configuration?.osVersion && {
                id: definition?.configuration?.osVersion.id,
              },
            }),
            ...(hasRemoteKeyInjection
              ? {
                  remoteKeyInjection:
                    getRemoteKeyInjectionV2(definition?.configuration?.remoteKeyInjection ?? []) ?? [],
                }
              : { remoteKeyInjection: [] }),
            ...(hasSelectedSysPackages
              ? {
                  systemPackageVersions: getSystemPackageVersionsV2(
                    definition?.configuration?.systemPackageVersions ?? [],
                  ),
                }
              : { systemPackageVersions: [] }),
          },
        },
      };
      return data;
    },
    [buildConditionsV2, getApplicationVersionsV2, getRemoteKeyInjectionV2, getSystemPackageVersionsV2],
  );

  return {
    handleSuccess,
    handleError,
    buildConfigurationRequestData,
    buildConfigurationRequestDataForV2,
    duplicateConfigurationRequestDataForV2,
  };
}
